import { api } from "./client";

export type ServiceModuleStep = {
  title: string;
  description: string; // empty string → no paragraph rendered
  items: string[];
};

export type ServiceModule = {
  // buttonLabel must be unique within a service (used as the list key)
  buttonLabel: string;
  title: string;
  eyebrow: string; // empty string → no eyebrow rendered
  items: string[];
  // Non-empty → modal renders a numbered step breakdown instead of `items`.
  steps?: ServiceModuleStep[];
};

export type ServiceContent = {
  key: string;
  title: string;
  titleMobile?: string;
  subtitle: string;
  body: string; // paragraphs separated by \n\n; bullet lines by \n
  price: number;
  currency: string;
  duration: string;
  ctaLabel: string;
  ctaUrl: string;
  // When non-empty, Book stores the request and redirects here (scheduling +
  // payment on Calendly); the inquiry button is hidden.
  calendlyUrl: string;
  // Optional rich content (sacred-visit); empty/"" for card-only tiers.
  format: string; // replaces the Session stat when non-empty
  formatLabel: string; // per-service Format-stat label; "" → shared label
  modules: ServiceModule[];
  // Booking modal copy (admin-editable; screen + email share paymentSuccess).
  bookLabel: string;
  inquiryLabel: string;
  paymentSuccess: string;
  inquirySuccess: string;
  // Hero eyebrow + white pricing-card content (per-service hero pages).
  eyebrow: string;
  cardRows: { label: string; value: string }[];
  billingNote: string; // "" → no tag after USD
  checkoutNote: string; // "" → no note under the CTA
};

export type DeckContent = {
  key: string;
  eyebrow: string;
  title: string;
  subtitle: string;
  bullets: string[];
  /** Markdown. When set, the decks page renders it instead of bullets. */
  body: string;
  /** Markdown. Optional note shown under the body behind a diamond divider. */
  note: string;
  price: number;
  currency: string;
  buyLabel: string;
  buyUrl: string;
  readingLabel: string;
};

export type PromoModalContent = {
  id: number;
  triggerLabel: string;
  title: string;
  body: string; // Markdown
  image: string | null; // absolute URL or null
  buttonLabel: string;
  buttonUrl: string; // empty → button just closes the modal
  autoOpen: boolean;
  // Seconds to wait after load before auto-opening; 0 = immediately.
  autoOpenDelay: number;
  updatedAt: string;
};

export type SiteContent = {
  services: ServiceContent[];
  decks: DeckContent[];
  // Flat maps keyed by stable server-side keys (e.g. "hero.tagline",
  // "social.youtube"); read via the text()/link() selectors in hooks/useContent.
  texts: Record<string, string>;
  links: Record<string, string>;
  // Admin-set AUD→USD multiplier for the informational price subtitle; 0 = hide.
  usdRate: number;
  // Active admin-authored promo modal template, or null when none is active.
  promoModal: PromoModalContent | null;
};

export async function getContent(lang?: string): Promise<SiteContent> {
  const { data } = lang
    ? await api.get<SiteContent>("/api/content/", { params: { lang } })
    : await api.get<SiteContent>("/api/content/");
  return data;
}

export type LegalDocContent = {
  slug: string;
  title: string;
  lastUpdated: string;
  body: string; // Markdown
};

export async function getLegalDoc(
  slug: string,
  lang?: string,
): Promise<LegalDocContent> {
  // The backend resolves language from ?lang= (resolve_lang ignores the
  // Accept-Language header), so pass it explicitly like getContent does.
  const { data } = await api.get<LegalDocContent>(`/api/content/legal/${slug}/`, {
    params: lang ? { lang } : undefined,
  });
  return data;
}

export type CardMeaning = {
  id: number;
  general: string;
  relationships: string;
  finance: string;
};

export async function getDeckCardMeanings(
  deck: string,
  lang?: string,
): Promise<CardMeaning[]> {
  // The backend resolves language from ?lang= (resolve_lang ignores the
  // Accept-Language header), so pass it explicitly like getContent does.
  const { data } = await api.get<CardMeaning[]>(`/api/content/cards/${deck}/`, {
    params: lang ? { lang } : undefined,
  });
  return data;
}

export async function getDeckCardMeaningsPdf(
  deck: string,
  lang?: string,
  onProgress?: (fraction: number | null) => void,
): Promise<Blob> {
  // Backend resolves language from ?lang= (it ignores Accept-Language), so
  // pass it explicitly. The endpoint streams application/pdf bytes.
  // onProgress reports the download fraction (0..1), or null while the total
  // size is still unknown, so the button can show a real progress bar.
  const { data } = await api.get<Blob>(`/api/content/cards/${deck}/pdf/`, {
    params: lang ? { lang } : undefined,
    responseType: "blob",
    onDownloadProgress: onProgress
      ? (e) => onProgress(e.total ? e.loaded / e.total : null)
      : undefined,
  });
  return data;
}
